import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { PageId } from 'records/Page';
import { DmsProviderKind, ProcessingStatus } from 'records/ProcessingStatus';
import { getProcessingStatuses } from 'services/processingStatusService';
import * as toast from 'utils/toast';

export interface ProcessingStatusActions {
  getProcessingStatuses: (
    actionParams: AppStateActionParams,
    toastText?: ProcessingStatusActionsToastText,
    onlySubmittals?: boolean
  ) => Promise<AppState>;

  clearProcessingStatuses: (actionParams: AppStateActionParams) => AppState;
}

interface ProcessingStatusActionsToastText {
  fileUploadError: {
    title: string;
    message: string;
  };

  drawingsReadyForReview: {
    title: string;
    message: string;
  };

  fileUploadSuccess: {
    title: string;
    message: string;
  };
}

const processingStatusActions: ProcessingStatusActions = {
  getProcessingStatuses: async (
    { state }: AppStateActionParams,
    toastText,
    onlySubmittals
  ): Promise<AppState> => {
    const { selectedProject } = state();

    const newlyFetchedStatuses = await getProcessingStatuses(
      selectedProject,
      onlySubmittals
    ).then((ps) =>
      ps
        .toList()
        .filter((s) => s.uploadedBy !== 'test')
        .filter((s) => s.dmsProviderKind !== DmsProviderKind.STORAGE)
    );

    const newSortedStatuses = newlyFetchedStatuses.sort(
      (ps) => ps.uploadTimestamp
    );

    const originalStatusCount = state().processingStatuses.size;

    const newStatuses = newlyFetchedStatuses.filter((nfs) => {
      const oldStatusMatch = state()
        .processingStatuses.filter(
          (ps) => ps.processingRequestid === nfs.processingRequestid
        )
        .toList()
        .first(null);
      return oldStatusMatch === null || oldStatusMatch?.status !== nfs.status;
    });
    const originalErrorStatusIDs = state()
      .processingStatuses.filter((ps) => ps.status === 'Error')
      .map((ps) => ps.processingRequestid);

    const originalDmsStatusIDs = state()
      .processingStatuses.filter(
        (ps) => ps.dmsProviderKind !== DmsProviderKind.NONE
      )
      .map((ps) => ps.processingRequestid);

    const newDmsStatusIDs = newlyFetchedStatuses
      .filter(
        (ps) =>
          !originalDmsStatusIDs.contains(ps.processingRequestid) &&
          ps.dmsProviderKind !== DmsProviderKind.NONE
      )
      .map((ps) => ps.processingRequestid);

    const newErrorStatusIDs = newlyFetchedStatuses
      .filter((ps) => ps.status === 'Error')
      .map((ps) => ps.processingRequestid);

    const originalRfrStatusIDs = state()
      .processingStatuses.filter((ps) => ps.status === 'Ready for Review')
      .map((ps) => ps.processingRequestid);
    // If there are new errors show the toast to the user
    if (
      state().processingStatuses.size > 0 &&
      originalErrorStatusIDs.size < newErrorStatusIDs.size &&
      originalStatusCount > 0
    ) {
      const newDmsErrorStatusIDs = newlyFetchedStatuses
        .filter(
          (ps) =>
            ps.status === 'Error' &&
            ps.dmsProviderKind !== DmsProviderKind.NONE &&
            !originalErrorStatusIDs.contains(ps.processingRequestid)
        )
        .map((ps) => ps.processingRequestid);

      if (newDmsErrorStatusIDs.size > 0) {
        newDmsErrorStatusIDs.map((id) => {
          const providerTypeLabel =
            newlyFetchedStatuses
              .find((v) => v.processingRequestid === id)
              ?.dmsProviderKind?.toString() || '';
          toast.error(
            toastText?.fileUploadError.message,
            `${providerTypeLabel}: ${toastText?.fileUploadError.title}`
          );
        });
      }
      const nonDmsErrorStatusIDs = newlyFetchedStatuses
        .filter(
          (ps) =>
            ps.status === 'Error' &&
            ps.dmsProviderKind === DmsProviderKind.NONE &&
            !originalErrorStatusIDs.contains(ps.processingRequestid)
        )
        .map((ps) => ps.processingRequestid);

      if (nonDmsErrorStatusIDs.size > 0) {
        toast.error(
          toastText?.fileUploadError.message,
          toastText?.fileUploadError.title
        );
      }
    } else if (originalStatusCount > 0) {
      // Check new for new requests that are complete
      const newRfr = newlyFetchedStatuses
        .filter((ps) => ps.status === 'Ready for Review')
        .map((ps) => ps.processingRequestid);

      const newProcessingDmsStatuses = newStatuses.filter(
        (ps) =>
          (newDmsStatusIDs.contains(ps.processingRequestid) &&
            ps.status === 'Ready for Review') ||
          ps.status === 'Processing'
      );

      if (newProcessingDmsStatuses.size > 0) {
        newProcessingDmsStatuses.map((status) => {
          const providerTypeLabel =
            newlyFetchedStatuses
              .find((v) => v.processingRequestid === status.processingRequestid)
              ?.dmsProviderKind?.toString() || '';

          if (providerTypeLabel !== DmsProviderKind.NONE) {
            toast.success(
              toastText?.fileUploadSuccess.message,
              `${providerTypeLabel}: ${toastText?.fileUploadSuccess.title}`
            );
          }
        });
      } else if (
        state().processingStatuses.size > 0 &&
        originalRfrStatusIDs.size < newRfr.size &&
        newProcessingDmsStatuses.size < newRfr.size
      ) {
        toast.info(
          toastText?.drawingsReadyForReview.message,
          toastText?.drawingsReadyForReview.title
        );
      }
    }

    return state().update('processingStatuses', (processingStatuses) => {
      return newSortedStatuses.reduce((acc, ps) => {
        return acc.set(ps.processingRequestid, ps);
      }, processingStatuses);
    });
  },

  clearProcessingStatuses: ({ state }: AppStateActionParams): AppState => {
    return state().set(
      'processingStatuses',
      Immutable.OrderedMap<PageId, ProcessingStatus>()
    );
  },
};

export default processingStatusActions;
